import { useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { authTokenAtom } from "atoms/authAtoms";
import { stageConfigAtom } from "atoms/Rtool/stageConfigAtoms";
import { errorAtom } from "atoms/error";
import { EachConditionData, EachConditionDataKeys, setEachConditionDataAtom } from "atoms/Rtool/cabinAtoms";
import { CABIN_NOISE_EACH_IDS } from "utils/consts";
import { useResetEachConditionData } from "./initializer";
import { fetchGraphData, fetchGraphDataHev } from "api/fetchRtool";
import { powertrainAtom } from "atoms/powertrainAtom";
import { gearIdAtom } from "atoms/gearAtom";

export function useEachConditionDataSetter() {
    const authToken = useAtomValue(authTokenAtom);
    const config = useAtomValue(stageConfigAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const gearId = useAtomValue(gearIdAtom);
    const isHev = powertrain === "hev";
    const setEachConditionData = useSetAtom(setEachConditionDataAtom);
    const setError = useSetAtom(errorAtom);
    const resetData = useResetEachConditionData();

    // ローディング状態の初期データをセット
    useEffect(() => {
        resetData();
    }, [resetData]);

    // API Call
    useEffect(() => {
        if (!config.project_id || !authToken) return;
        if (isHev && !gearId) return;

        (async () => {
            try {
                const results: EachConditionData[] = await Promise.all(
                    (CABIN_NOISE_EACH_IDS as EachConditionDataKeys[]).map(async (id) => {
                        try {
                            const data = isHev
                                ? await fetchGraphDataHev(
                                    authToken,
                                    { ...config, graph_id: id },
                                    gearId!
                                )
                                : await fetchGraphData(
                                    authToken,
                                    { ...config, graph_id: id }
                                );
                            return { graph_id: id, data, status: "success" } as const;
                        } catch (err) {
                            console.error(`fetchGraphData failed for ${id}:`, err);
                            return {
                                graph_id: id,
                                data: null,
                                status: "error",
                            } as const;
                        };
                    })
                );

                setEachConditionData(...results);
            } catch (unhandled) {
                setError(`Unexpected error in fetching all data: ${unhandled}`);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        authToken,
        config,
        isHev,
        gearId,
        setEachConditionData,
        setError,
    ]);
};
